import { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { API_BASE } from "../../utils/api";
import { getUserPhotoUrl } from "../../utils/config";
import { useLanguage } from "../../context/LanguageContext";

/* =========================
   HELPERS
========================= */
const formatDateForInput = (date) => {
    if (!date) return "";
    const d = new Date(date);
    return d.toISOString().split("T")[0];
};

export default function ProfilePersonal() {
    const history = useHistory();
    const user = JSON.parse(localStorage.getItem("user"));

    const [form, setForm] = useState({
        username: user.userId || "",
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        email: user.eMaile || "",
        phone: user.phone || "",
        birthDate: formatDateForInput(user.birthDate),
        photo: null,
        photoPreview: getUserPhotoUrl(user.userId, true),

        // 🔐 password (opcional)
        password: "",
        confirmPassword: "",
    });
    const { t, switchLanguage, language } = useLanguage();
    const [errors, setErrors] = useState({});
    const [branchLabel, setBranchLabel] = useState("Matriz");

    // localStorage["user"] es el snapshot de cuando se inicio sesion — puede estar
    // desactualizado si el dato cambio despues (por el propio usuario en otra
    // sesion, o por el Aprobador desde Gestion de usuarios). Se trae el registro
    // actual del backend para no pisar datos nuevos con el formulario vacio/viejo.
    useEffect(() => {
        if (!user?.idEmployee) return;

        Promise.all([
            fetch(`${API_BASE}/api-ultra/api/ws/ecommerce_users?id_employee=${user.idEmployee}`).then((r) => r.json()),
            fetch(`${API_BASE}/api-ultra/api/ws/parameters_cust_ship?id_employee=${user.idEmployee}&myaccount_id=${user.myaccountId}&has_ship_to=true&id_customer=${user.idCustomer}`).then((r) => r.json()),
        ])
            .then(([list, shipTos]) => {
                const data = list && list.length ? list[0] : null;
                if (data) {
                    setForm((prev) => ({
                        ...prev,
                        username: data.userId || prev.username,
                        firstName: data.firstName ?? prev.firstName,
                        lastName: data.lastName ?? prev.lastName,
                        email: data.eMaile ?? data.eMail ?? prev.email,
                        phone: data.phone ?? data.homePhone ?? prev.phone,
                        birthDate: formatDateForInput(data.birthDate) || prev.birthDate,
                    }));
                }

                // Sucursal: solo informativa aqui (se reasigna desde Gestion de
                // usuarios, no desde el propio perfil). idShipTo null = Matriz.
                const idShipTo = data?.idShipTo ?? data?.id_ship_to ?? null;
                if (idShipTo) {
                    const match = (Array.isArray(shipTos) ? shipTos : [])
                        .find((s) => String(s.idShipTo) === String(idShipTo));
                    setBranchLabel(match ? match.nameShipTo : `Sucursal ${idShipTo}`);
                } else {
                    setBranchLabel("Matriz");
                }
            })
            .catch((err) => console.error("Error cargando perfil", err));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /* =========================
       HANDLERS
    ========================== */
    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
        setErrors({ ...errors, [e.target.name]: null });
    };

    const handlePhotoChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setForm({
            ...form,
            photo: file,
            photoPreview: URL.createObjectURL(file),
        });
    };

    /* =========================
       VALIDATION
    ========================== */
    const validate = () => {
        const e = {};

        // 🔐 validar contraseña solo si escribe algo
        if (form.password || form.confirmPassword) {
            if (!form.password) e.password = "Ingrese nueva contraseña";
            if (!form.confirmPassword) e.confirmPassword = "Confirme la contraseña";
            if (form.password !== form.confirmPassword)
                e.confirmPassword = "Las contraseñas no coinciden";
            if (form.password && form.password.length < 6)
                e.password = "Mínimo 6 caracteres";
        }

        setErrors(e);
        return Object.keys(e).length === 0;
    };

    /* =========================
       SAVE
    ========================== */
    const handleSave = async () => {
        if (!validate()) return;

        const formData = new FormData();

        // =====================
        // JSON USUARIO
        // =====================
        const userJson = {
            userId: user.userId,
            firstName: form.firstName,
            lastName: form.lastName,
            eMail: form.email,
            homePhone: form.phone,
            birthDate: form.birthDate,
        };

        // 🔐 enviar password solo si viene
        if (form.password) {
            userJson.password = form.password;
        }

        formData.append("user", JSON.stringify(userJson));

        // =====================
        // FOTO
        // =====================
        if (form.photo) {
            formData.append("photo", form.photo);
        }

        const res = await fetch(
            `${API_BASE}/customers-ultra/api/ws/update_personal?id_employee=${user.idEmployee}`,
            {
                method: "POST",
                body: formData, // SIN headers
            }
        );

        if (!res.ok) {
            throw new Error("Error actualizando perfil");
        }

        // 🔄 actualizar localStorage
        localStorage.setItem(
            "user",
            JSON.stringify({
                ...user,
                firstName: form.firstName,
                lastName: form.lastName,
                eMaile: form.email,
                phone: form.phone,
                birthDate: form.birthDate,
                photoUrl: form.photoPreview,
            })
        );

        setTimeout(() => {
            history.goBack();
        }, 300);
    };

    /* =========================
       RENDER
    ========================== */
    return (
        <div className="max-w-5xl mx-auto mt-24 px-4">

            {/* HEADER */}
            <div className="flex justify-between items-center mb-8">
                <div>
                    <h1 className="text-2xl font-semibold">Perfil personal</h1>
                    <p className="text-gray-500">
                        Administra tu información personal y seguridad
                    </p>
                </div>

                <button
                    onClick={() => history.goBack()}
                    className="px-4 py-2 border rounded hover:bg-gray-100"
                >
                    ←  {t.btnBack}
                </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">

                {/* FOTO */}
                <div className="bg-white border rounded-xl p-6 text-center">
                    <img
                        src={form.photoPreview}
                        alt="Foto de perfil"
                        onError={(e) => { e.currentTarget.src = "https://www.ultra-erp.pro/images/user_default.jpg"; }}
                        className="w-32 h-32 rounded-full object-cover mx-auto mb-4"
                    />





                    <label className="cursor-pointer text-sm text-brand-primary">
                        Cambiar foto
                        <input
                            type="file"
                            accept="image/*"
                            onChange={handlePhotoChange}
                            className="hidden"
                        />
                    </label>
                </div>

                {/* FORM */}
                <div className="md:col-span-2 bg-white border rounded-xl p-6 space-y-6">

                    {/* DATOS */}
                    <Section title="Datos personales">
                        <Grid>
                            <Input
                                label="Nombres"
                                name="firstName"
                                value={form.firstName}
                                onChange={handleChange}
                            />
                            <Input
                                label="Apellidos"
                                name="lastName"
                                value={form.lastName}
                                onChange={handleChange}
                            />
                        </Grid>

                        <div>
                            <label className="block text-sm text-gray-600 mb-1">Usuario</label>
                            <input
                                value={form.username}
                                disabled
                                readOnly
                                className="w-full border border-blue-200 bg-blue-50/50 text-blue-800 font-medium
                                    px-3 py-2 rounded cursor-not-allowed focus:outline-none"
                            />
                        </div>

                        <Grid>
                            <Input
                                label="Email"
                                name="email"
                                value={form.email}
                                onChange={handleChange}
                            />
                            <Input
                                label="Celular"
                                name="phone"
                                value={form.phone}
                                onChange={handleChange}
                            />
                        </Grid>

                        <Input
                            label="Sucursal"
                            value={branchLabel}
                            disabled
                            readOnly
                        />
                    </Section>


                    <Section title="Cambiar contraseña">
                        <Grid>
                            <Input
                                type="password"
                                label="Nueva contraseña"
                                name="password"
                                value={form.password}
                                onChange={handleChange}
                                error={errors.password}
                            />

                            <Input
                                type="password"
                                label="Confirmar contraseña"
                                name="confirmPassword"
                                value={form.confirmPassword}
                                onChange={handleChange}
                                error={errors.confirmPassword}
                            />
                        </Grid>
                        <p className="text-xs text-gray-500">
                            Deja estos campos vacíos si no deseas cambiar tu contraseña
                        </p>
                    </Section>

                    <div className="pt-4">
                        <button
                            onClick={handleSave}
                            className="bg-brand-primary text-white px-6 py-2 rounded hover:opacity-90"
                        >
                            Guardar cambios
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

/* =========================
   UI HELPERS
========================= */

function Section({ title, children }) {
    return (
        <div className="space-y-4">
            <h2 className="text-sm font-semibold text-gray-700">{title}</h2>
            {children}
        </div>
    );
}

function Grid({ children }) {
    return <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>;
}

function Input({ label, error, ...props }) {
    return (
        <div>
            <label className="block text-sm text-gray-600 mb-1">
                {label}
            </label>
            <input
                {...props}
                className={`w-full border px-3 py-2 rounded focus:outline-none
                    ${error ? "border-red-500" : "border-gray-300"}`}
            />
            {error && (
                <p className="mt-1 text-xs text-red-600">{error}</p>
            )}
        </div>
    );
}
